/**
 * The volume waterfall — PLAN §5.7's *"Displayed as a visual waterfall in the
 * equipment panel"*.
 *
 * Five bars, each shorter than the last, with the loss between them named. The
 * bar is proportional to the water that went in, so the shape reads as
 * "everything you measure out, and where it goes" rather than as five unrelated
 * numbers.
 *
 * The loss segment is drawn in the same bar, in a warmer tint, immediately after
 * the volume that survives — so the eye sees the *step down* rather than having
 * to subtract two numbers. That is the whole reason this exists instead of a
 * table.
 */

import type { EquipmentProfileDoc } from '@beerwright/commands';
import { useMemo } from 'react';
import { previewWaterfall, waterfallStages } from '@/lib/equipment';
import { formatQuantity, unitLabel } from '@/lib/units';
import { useUnits } from '@/store/workspace-context';

export interface VolumeWaterfallChartProps {
  readonly profile: EquipmentProfileDoc;
  readonly batchVolumeL: number;
  readonly grainKg: number;
}

export function VolumeWaterfallChart({
  profile,
  batchVolumeL,
  grainKg,
}: VolumeWaterfallChartProps) {
  const units = useUnits();
  const stages = useMemo(
    () => waterfallStages(previewWaterfall(profile, batchVolumeL, grainKg), units),
    [profile, batchVolumeL, grainKg, units],
  );

  // Scale to the widest bar rather than to the batch: a profile with a large
  // top-up can put more water in than the first stage, and a bar running off
  // the edge would be worse than a slightly compressed chart.
  const max = Math.max(...stages.map((stage) => stage.volumeL + stage.lossL), 1);

  return (
    <ul aria-label="Volume waterfall" className="flex flex-col gap-2">
      {stages.map((stage) => (
        <li key={stage.key} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[11px] font-medium">{stage.label}</span>
            <span className="font-mono text-[11px] tabular-nums">
              {formatQuantity(stage.volumeL, 'volume', units)}
              <span className="ml-0.5 text-muted-foreground">{unitLabel('volume', units)}</span>
            </span>
          </div>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-l-full bg-foreground/30"
              style={{ width: `${percent(stage.volumeL, max)}%` }}
            />
            {stage.lossL > 0 && (
              <div
                className="h-full bg-warning/40"
                style={{ width: `${percent(stage.lossL, max)}%` }}
                title={stage.detail}
              />
            )}
          </div>
          {stage.lossL > 0 && (
            <p className="text-[10px] leading-tight text-muted-foreground">
              −{formatQuantity(stage.lossL, 'volume', units)} {unitLabel('volume', units)} ·{' '}
              {stage.detail}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A width percentage, clamped so a nonsensical profile cannot break the layout. */
function percent(value: number, max: number): number {
  return Math.min(100, Math.max(0, (value / max) * 100));
}
